import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Introduction from './pages/Introduction';
import Syllabus from './pages/Syllabus';
import Nouns from './pages/Nouns';
import Pronouns from './pages/Pronouns';
import CulturalApplications from './pages/CulturalApplications';
import Exercises from './pages/Exercises';
import AudioGallery from './pages/AudioGallery';
import Reflection from './pages/Reflection';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Certificate from './pages/Certificate';
import TeacherHub from './pages/TeacherHub';
import MatchingGame from './pages/MatchingGame';
import WritingPractice from './pages/WritingPractice';
import HierarchyGame from './pages/HierarchyGame';
import Printables from './pages/Printables';
import './App.css';

function App() {
  const [currentSection, setCurrentSection] = useState('introduction');
  const [user, setUser] = useState(null);

  // Initial Load
  useEffect(() => {
    const savedUser = localStorage.getItem('amharic-user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Update user progress logic
  const updateProgress = (section) => {
    if (!user) return;
    
    // Update progress
    const newProgress = { ...user.progress, [section]: true };
    
    // Calculate if complete
    const learningModules = [
      'introduction', 'nouns', 'pronouns', 'cultural', 
      'exercises', 'audio', 'reflection', 'teacher-hub'
    ];
    const completedCount = learningModules.filter(mod => newProgress[mod]).length;
    const isComplete = completedCount === learningModules.length;

    // Update User State
    const newUser = {
      ...user,
      progress: newProgress,
      xp: user.xp + 10, // Add simple XP for visiting/completing sections
      completed: isComplete,
      dateCompleted: isComplete && !user.dateCompleted ? new Date().toISOString() : user.dateCompleted
    };
    setUser(newUser);
    localStorage.setItem('amharic-user', JSON.stringify(newUser));
  };

  const handleLogin = (userData) => {
    // Check if user exists in local storage to restore progress, otherwise create new
    // For this simple demo, we just overwrite or create new session
    const newUser = {
      name: userData.name,
      avatar: userData.avatar,
      progress: {
        introduction: false,
        nouns: false,
        pronouns: false,
        cultural: false,
        exercises: false,
        audio: false,
        reflection: false,
        'teacher-hub': false
      },
      xp: 0,
      streak: 1,
      completed: false,
      dateJoined: new Date().toISOString()
    };
    setUser(newUser);
    localStorage.setItem('amharic-user', JSON.stringify(newUser));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('amharic-user');
    setCurrentSection('introduction');
  };

  return (
    <Router>
      {/* Login Overlay - Shows when no user is logged in */}
      {!user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <Login onLogin={handleLogin} />
        </div>
      )}
      
      <Layout 
        currentSection={currentSection} 
        setCurrentSection={setCurrentSection}
        progress={user ? user.progress : {}}
        user={user}
        onLogout={handleLogout}
      >
        <Routes>
          <Route path="/" element={<Introduction updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/syllabus" element={<Syllabus updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/nouns" element={<Nouns updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/pronouns" element={<Pronouns updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/cultural" element={<CulturalApplications updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/exercises" element={<Exercises updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/audio" element={<AudioGallery updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/reflection" element={<Reflection updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          
          {/* Teacher Hub Routes */}
          <Route path="/teacher-hub" element={<TeacherHub updateProgress={updateProgress} setCurrentSection={setCurrentSection} />} />
          <Route path="/teacher-hub/matching" element={<MatchingGame />} />
          <Route path="/teacher-hub/writing" element={<WritingPractice />} />
          <Route path="/teacher-hub/hierarchy" element={<HierarchyGame />} />
          <Route path="/teacher-hub/printables" element={<Printables />} />

          {/* Protected Routes - Redirect to Home if no user */}
          <Route path="/profile" element={user ? <Profile user={user} setCurrentSection={setCurrentSection} onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/certificate" element={user ? <Certificate user={user} /> : <Navigate to="/" />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;